import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Info, Search } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/** `/settings/ai-instructions` (v0.3.x AIコメント カスタム指示)。
 *  設計: docs/design/v03-ai-comment-custom-instructions.md §8-1。
 *  タブ自体は tenant_features `ai_custom_instructions` が ON のときのみ SettingsLayout に現れる。 */

type SelectionMode = 'tenant' | 'node';

type Instruction = {
  instruction_id: string;
  title: string;
  body: string;
  is_default_enabled: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

type InstructionsResponse = {
  selection_mode: SelectionMode;
  max_instructions: number;
  can_manage: boolean;
  /** 適用モードの変更可否 (tenant_features を書き換えるため会社管理者ロールも必要) */
  can_change_mode: boolean;
  instructions: Instruction[];
};

type NodeAssignmentsResponse = { node_id: string; instruction_ids: string[] };

const TITLE_MAX = 60;
const BODY_MAX = 1000;
/** 659店舗規模テナントでも一覧描画が重くならないよう、一度に描画するノード数の上限 (超過分は検索で絞る) */
const NODE_LIST_LIMIT = 100;

const MODE_LABELS: Record<SelectionMode, string> = {
  tenant: 'テナント共通 (どの店舗でも同じ指示を使う)',
  node: '組織ノード別 (店舗・ブランドごとに使う指示を変える)',
};

/** is_default_enabled の呼び名。Designレビュー2巡目 #3: バッジ / チェックボックスラベル /
 *  aria-label / モーダルで3通りの表記に割れていたため1箇所に集約する。 */
const DEFAULT_FLAG_LABEL: Record<SelectionMode, string> = {
  tenant: '常に使う',
  node: '未割当ノードの既定',
};

/** line-clamp-3 で実際に切り詰められる見込みかどうかの近似 (短文でトグルを出さないため)。 */
const isBodyClampable = (body: string) => body.length > 90 || body.split('\n').length > 3;

type DraftInstruction = {
  instruction_id: string | null;
  title: string;
  body: string;
  is_default_enabled: boolean;
};

export function AiInstructionsSettingsPage() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<DraftInstruction | null>(null);
  const [deleting, setDeleting] = useState<Instruction | null>(null);
  const [assignNodeId, setAssignNodeId] = useState<string | null>(null);
  const [nodeQuery, setNodeQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery<InstructionsResponse>({
    queryKey: ['ai-instructions'],
    queryFn: () => apiFetch<InstructionsResponse>('/ai-instructions'),
  });

  const { data: nodesData } = useQuery<NodesResponse>({
    queryKey: ['nodes'],
    queryFn: () => apiFetch<NodesResponse>('/nodes'),
    staleTime: 5 * 60 * 1000,
  });

  const mode = data?.selection_mode ?? 'tenant';
  const canManage = data?.can_manage ?? false;
  const canChangeMode = data?.can_change_mode ?? false;
  const instructions = useMemo(() => data?.instructions ?? [], [data]);
  const maxInstructions = data?.max_instructions ?? 10;

  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  const effectiveAssignNodeId = assignNodeId ?? flatNodes[0]?.node.node_id ?? null;
  const filteredNodes = useMemo(() => {
    const q = nodeQuery.trim().toLowerCase().replace(/[\s　]+/g, '');
    if (!q) return flatNodes;
    return flatNodes.filter(({ node }) =>
      node.name.toLowerCase().replace(/[\s　]+/g, '').includes(q)
      || node.node_code.toLowerCase().includes(q));
  }, [flatNodes, nodeQuery]);
  const visibleNodes = useMemo(() => filteredNodes.slice(0, NODE_LIST_LIMIT), [filteredNodes]);
  const isFiltering = nodeQuery.trim().length > 0;

  const toggleExpanded = (id: string) => setExpandedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const { data: assignments } = useQuery<NodeAssignmentsResponse>({
    queryKey: ['ai-instructions', 'node-assignments', effectiveAssignNodeId],
    queryFn: () => apiFetch<NodeAssignmentsResponse>(`/ai-instructions/node-assignments?node_id=${effectiveAssignNodeId}`),
    enabled: mode === 'node' && !!effectiveAssignNodeId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['ai-instructions'] });

  const modeMut = useMutation({
    mutationFn: (selection_mode: SelectionMode) =>
      apiFetch('/ai-instructions/settings', { method: 'PUT', body: JSON.stringify({ selection_mode }) }),
    onSuccess: () => { invalidate(); toast.success('適用モードを変更しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '変更に失敗しました'),
  });

  const saveMut = useMutation({
    mutationFn: (d: DraftInstruction) => {
      const payload = { title: d.title.trim(), body: d.body.trim(), is_default_enabled: d.is_default_enabled };
      return d.instruction_id
        ? apiFetch(`/ai-instructions/${d.instruction_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/ai-instructions', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { invalidate(); setDraft(null); toast.success('カスタム指示を保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const toggleDefaultMut = useMutation({
    mutationFn: (v: { id: string; is_default_enabled: boolean }) =>
      apiFetch(`/ai-instructions/${v.id}`, { method: 'PUT', body: JSON.stringify({ is_default_enabled: v.is_default_enabled }) }),
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '変更に失敗しました'),
  });

  // QA M-1 / design-and-code H-3 / Test #3: 1件ずつ sort_order を交換する方式は
  // sort_order 重複時に無言no-opになり、2本の並列PUTで順序も壊れうる。配列一括PUTに変更した。
  const reorderMut = useMutation({
    mutationFn: (instruction_ids: string[]) =>
      apiFetch('/ai-instructions/order', { method: 'PUT', body: JSON.stringify({ instruction_ids }) }),
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '並び替えに失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/ai-instructions/${id}`, { method: 'DELETE' }),
    onSuccess: () => { invalidate(); setDeleting(null); toast.success('カスタム指示を削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleting(null); },
  });

  const assignMut = useMutation({
    mutationFn: (v: { node_id: string; instruction_ids: string[] }) =>
      apiFetch('/ai-instructions/node-assignments', { method: 'PUT', body: JSON.stringify(v) }),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['ai-instructions', 'node-assignments', v.node_id] });
      toast.success('割当を更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '割当の更新に失敗しました'),
  });

  /** 配列上で1つ入れ替えた並びを丸ごと送る (サーバ側が index で採番し直す)。 */
  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= instructions.length) return;
    const next = instructions.map((i) => i.instruction_id);
    [next[index], next[target]] = [next[target]!, next[index]!];
    reorderMut.mutate(next);
  };

  // design-and-code H-2: assignments 未ロード/取得失敗時に操作すると `?? []` を基点にした
  // 全置換PUTが飛び、既存割当を丸ごと消してしまう。ロード済みのときだけ操作を許可する。
  const assignmentsReady = assignments !== undefined;
  const toggleAssignment = (instructionId: string) => {
    if (!effectiveAssignNodeId || !assignments) return;
    const current = assignments.instruction_ids;
    const next = current.includes(instructionId)
      ? current.filter((id) => id !== instructionId)
      : [...current, instructionId];
    assignMut.mutate({ node_id: effectiveAssignNodeId, instruction_ids: next });
  };

  const atLimit = instructions.length >= maxInstructions;
  const draftTitleTooLong = (draft?.title.trim().length ?? 0) > TITLE_MAX;
  const draftBodyTooLong = (draft?.body.trim().length ?? 0) > BODY_MAX;
  const draftValid = !!draft && draft.title.trim().length > 0 && draft.body.trim().length > 0
    && !draftTitleTooLong && !draftBodyTooLong;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-h2">AIコメント カスタム指示</h2>
        <p className="text-sm text-text-sub mt-1">
          ダッシュボードのAI月次コメントに、自社独自の分析観点を指示できます (最大{maxInstructions}件)。
          指示が長いほど1回あたりのAIトークン消費が増えます。
        </p>
      </div>

      {/* 適用モード */}
      <section className="border border-surface-border rounded-lg bg-white p-4 space-y-2">
        <h3 className="text-sm font-semibold text-text-body">適用モード</h3>
        <div className="max-w-md">
          <Select
            value={mode}
            onValueChange={(v) => modeMut.mutate(v as SelectionMode)}
            disabled={!canChangeMode || modeMut.isPending}
          >
            <SelectTrigger aria-label="カスタム指示の適用モード">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tenant">{MODE_LABELS.tenant}</SelectItem>
              <SelectItem value="node">{MODE_LABELS.node}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-text-sub flex items-start gap-1.5">
          <Info size={14} className="shrink-0 mt-0.5" aria-hidden />
          {mode === 'tenant'
            ? '「常に使う」をONにした指示がすべてのAIコメントに適用されます。Lite版のダッシュボードでは、生成のたびに使う指示を選び直すこともできます。'
            : '組織ノードごとに使う指示を割り当てます。割当が無いノードは「常に使う」をONにしたテナント既定の指示が使われます。'}
        </p>
      </section>

      {/* 指示一覧 */}
      <section className="space-y-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-semibold text-text-body">
            登録済みの指示 <span className="text-text-sub font-normal">{instructions.length} / {maxInstructions}</span>
          </h3>
          {canManage && (
            <div className="flex items-center gap-2 flex-wrap">
              {/* Design M-10: disabled ボタンの title ツールチップは多くのブラウザで出ないため可視テキストで理由を出す */}
              {atLimit && (
                <span className="text-xs text-text-sub">上限{maxInstructions}件に達しています。不要な指示を削除してください</span>
              )}
              <Button
                variant="primary"
                leftIcon={<Plus size={16} />}
                disabled={atLimit}
                onClick={() => setDraft({ instruction_id: null, title: '', body: '', is_default_enabled: mode === 'tenant' })}
              >
                指示を追加
              </Button>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="text-text-sub">読み込み中…</div>
        ) : instructions.length === 0 ? (
          <EmptyState
            message="カスタム指示がまだありません"
            description="「指示を追加」から、AIに重視してほしい分析観点を登録してください。"
          />
        ) : (
          <ul className="space-y-2">
            {instructions.map((ins, i) => {
              const expanded = expandedIds.has(ins.instruction_id);
              return (
                <li key={ins.instruction_id} className="border border-surface-border rounded-lg bg-white p-4">
                  {/* Design C-2: ボタン群を shrink-0 の非wrapにすると 320px で内寸を超える。
                      外側・内側とも flex-wrap にして折り返させる */}
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    {/* Design 2巡目 #9: sm(640px) だと 768〜900px でサイドバー出現と重なり本文欄が
                        224px程度まで潰れるため、1カラムに戻す境界を lg(1024px) にする */}
                    <div className="min-w-0 flex-1 basis-full lg:basis-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-text-body break-words">{ins.title}</span>
                        {/* Design H-5: mode='node' でも「常に使う」= 未割当ノードの既定として効くため常に表示する */}
                        {ins.is_default_enabled && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border">
                            {DEFAULT_FLAG_LABEL[mode]}
                          </span>
                        )}
                      </div>
                      {/* Design C-1: 本文全文レンダリングは 1000文字×10件×320px で一覧が崩壊するため既定は3行に抑える */}
                      <p className={`text-sm text-text-sub mt-1 whitespace-pre-wrap break-words ${expanded ? '' : 'line-clamp-3'}`}>
                        {ins.body}
                      </p>
                      {/* Design 2巡目 #7: 3行に収まる短い本文でトグルを出すと44pxの無駄になる */}
                      {isBodyClampable(ins.body) && (
                        <button type="button" onClick={() => toggleExpanded(ins.instruction_id)}
                          className="text-xs text-text-sub underline hover:text-text-body min-h-[44px] focus-ring"
                          aria-expanded={expanded}>
                          {expanded ? '本文を折りたたむ' : '本文をすべて表示'}
                        </button>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-1 flex-wrap">
                        <label className="flex items-center gap-1.5 text-xs text-text-sub mr-2 min-h-[44px]">
                          <input
                            type="checkbox"
                            className="w-5 h-5"
                            checked={ins.is_default_enabled}
                            disabled={toggleDefaultMut.isPending}
                            onChange={(e) => toggleDefaultMut.mutate({ id: ins.instruction_id, is_default_enabled: e.target.checked })}
                            aria-label={`「${ins.title}」を${DEFAULT_FLAG_LABEL[mode]}にする`}
                          />
                          {DEFAULT_FLAG_LABEL[mode]}
                        </label>
                        <button type="button" onClick={() => move(i, -1)} disabled={i === 0 || reorderMut.isPending}
                          className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body disabled:opacity-30 focus-ring"
                          aria-label={`「${ins.title}」を上へ`}>
                          <ChevronUp size={16} />
                        </button>
                        <button type="button" onClick={() => move(i, 1)} disabled={i === instructions.length - 1 || reorderMut.isPending}
                          className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body disabled:opacity-30 focus-ring"
                          aria-label={`「${ins.title}」を下へ`}>
                          <ChevronDown size={16} />
                        </button>
                        <button type="button"
                          onClick={() => setDraft({ instruction_id: ins.instruction_id, title: ins.title, body: ins.body, is_default_enabled: ins.is_default_enabled })}
                          className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"
                          aria-label={`「${ins.title}」を編集`}>
                          <Pencil size={16} />
                        </button>
                        <button type="button" onClick={() => setDeleting(ins)}
                          className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"
                          aria-label={`「${ins.title}」を削除`}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ノード別割当 */}
      {mode === 'node' && (
        <section className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
          <h3 className="text-sm font-semibold text-text-body">組織ノード別の割当</h3>
          {flatNodes.length === 0 ? (
            <p className="text-sm text-text-sub">組織ノードがありません。</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Design C-3: 659店舗規模テナント (commit 1f764da の性能事故) を踏まないよう、
                  全ノードを一度に展開する Select/ツリーは使わず、検索で絞り込んだ結果のみ描画する */}
              <div className="min-w-0">
                <label htmlFor="ai-instruction-node-search" className="block text-xs text-text-sub mb-1">
                  対象ノードを検索 <span className="text-text-sub">(全{flatNodes.length}件)</span>
                </label>
                <div className="relative">
                  <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-sub" aria-hidden />
                  <input
                    id="ai-instruction-node-search"
                    type="search"
                    value={nodeQuery}
                    onChange={(e) => setNodeQuery(e.target.value)}
                    placeholder="店舗名・コードで絞り込む"
                    className="w-full rounded border border-surface-border pl-7 pr-3 py-2 text-sm focus-ring"
                  />
                </div>
                <ul className="mt-2 max-h-64 overflow-y-auto border border-surface-border rounded divide-y divide-surface-border"
                  aria-label="対象ノード一覧">
                  {visibleNodes.map(({ node, depth }) => (
                    <li key={node.node_id}>
                      <button type="button" onClick={() => setAssignNodeId(node.node_id)}
                        aria-current={node.node_id === effectiveAssignNodeId}
                        className={`w-full text-left px-3 py-2 text-sm min-h-[44px] break-words focus-ring ${
                          node.node_id === effectiveAssignNodeId ? 'bg-surface-sunken font-semibold text-text-body' : 'text-text-sub hover:bg-surface-sunken'
                        }`}>
                        {/* Design 2巡目 #8: 絞り込み中は祖先が結果から消えるため、インデントを残すと
                            階層を誤読させる。絞り込み中はフラット表示にし、代わりに階層パスを添える */}
                        {isFiltering ? (
                          <span className="inline-block">
                            {node.name}
                            {node.node_code && <span className="text-xs text-text-sub ml-1">({node.node_code})</span>}
                          </span>
                        ) : (
                          <span style={{ paddingLeft: `${depth * 12}px` }} className="inline-block">{node.name}</span>
                        )}
                      </button>
                    </li>
                  ))}
                  {visibleNodes.length === 0 && (
                    <li className="px-3 py-2 text-sm text-text-sub">該当するノードがありません</li>
                  )}
                </ul>
                {/* Design 2巡目 #4: live region は条件付きマウントだとSRが読み上げないため常設し文言だけ差し替える */}
                <p className="text-xs text-text-sub mt-1 empty:hidden" aria-live="polite">
                  {filteredNodes.length > NODE_LIST_LIMIT
                    ? `${filteredNodes.length}件中 先頭${NODE_LIST_LIMIT}件を表示しています。検索で絞り込んでください`
                    : ''}
                </p>
              </div>
              <fieldset className="min-w-0">
                <legend className="text-xs text-text-sub mb-1">
                  「{flatNodes.find((n) => n.node.node_id === effectiveAssignNodeId)?.node.name ?? '—'}」で使う指示
                </legend>
                {instructions.length === 0 ? (
                  <p className="text-sm text-text-sub">先に指示を登録してください。</p>
                ) : (
                  <ul className="space-y-1">
                    {instructions.map((ins) => (
                      <li key={ins.instruction_id}>
                        <label className="flex items-start gap-2 text-sm min-h-[44px] py-1">
                          <input
                            type="checkbox"
                            className="mt-1 w-5 h-5"
                            checked={(assignments?.instruction_ids ?? []).includes(ins.instruction_id)}
                            disabled={!canManage || !assignmentsReady || assignMut.isPending}
                            onChange={() => toggleAssignment(ins.instruction_id)}
                          />
                          <span className="break-words">{ins.title}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-text-sub mt-2">
                  1件も選ばない場合、このノードでは「未割当ノードの既定」に指定した指示が使われます。
                </p>
              </fieldset>
            </div>
          )}
        </section>
      )}

      {/* 追加・編集モーダル */}
      <Modal
        open={!!draft}
        title={draft?.instruction_id ? 'カスタム指示を編集' : 'カスタム指示を追加'}
        onClose={() => setDraft(null)}
        footer={(
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!draftValid || saveMut.isPending}
              onClick={() => draft && saveMut.mutate(draft)}>
              保存
            </Button>
          </div>
        )}
      >
        {draft && (
          <div className="space-y-4">
            <div>
              <label htmlFor="ai-instruction-title" className="block text-sm font-medium text-text-body mb-1">タイトル</label>
              <input
                id="ai-instruction-title"
                type="text"
                maxLength={TITLE_MAX}
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="例: 原価率の改善余地を必ず指摘する"
                className="w-full rounded border border-surface-border px-3 py-2 text-sm focus-ring"
              />
              {/* Design M-11: 超過を色だけで伝えない (文言 + aria-live) */}
              <p className={`text-xs mt-1 ${draftTitleTooLong ? 'text-danger-text' : 'text-text-sub'}`}
                {...(draftTitleTooLong ? { role: 'alert' as const } : {})}>
                {draft.title.trim().length} / {TITLE_MAX} 文字{draftTitleTooLong ? ' — 上限を超えています' : ''}
              </p>
            </div>
            <div>
              <label htmlFor="ai-instruction-body" className="block text-sm font-medium text-text-body mb-1">指示内容</label>
              <textarea
                id="ai-instruction-body"
                rows={7}
                maxLength={BODY_MAX}
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                placeholder="例: 原価率が目標(30%)を超えている場合は、どの費目が押し上げているかに触れ、改善の打ち手を1つ提案してください。"
                className="w-full rounded border border-surface-border px-3 py-2 text-sm focus-ring"
              />
              <p className={`text-xs mt-1 ${draftBodyTooLong ? 'text-danger-text' : 'text-text-sub'}`}
                {...(draftBodyTooLong ? { role: 'alert' as const } : {})}>
                {draft.body.trim().length} / {BODY_MAX} 文字{draftBodyTooLong ? ' — 上限を超えています' : ''}
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm min-h-[44px]">
              <input
                type="checkbox"
                className="w-5 h-5"
                checked={draft.is_default_enabled}
                onChange={(e) => setDraft({ ...draft, is_default_enabled: e.target.checked })}
              />
              この指示を「{DEFAULT_FLAG_LABEL[mode]}」にする
              {mode === 'tenant' && <span className="text-text-sub">（すべてのAIコメントに適用）</span>}
            </label>
            <p className="text-xs text-text-sub">
              指示は「分析の観点」の指定として扱われます。出力言語 (日本語)・コメント本文のみを出力するというルール、
              およびデータに無い数値を作らないというルールは、指示の内容にかかわらず常に優先されます。
            </p>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="カスタム指示を削除"
        message={`「${deleting?.title ?? ''}」を削除します。ノードへの割当も同時に削除されます。この操作は取り消せません。`}
        confirmLabel="削除する"
        destructive
        loading={deleteMut.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.instruction_id)}
      />
    </div>
  );
}
